// The Code workspace's real, multi-file HollowScript editor (the
// general-purpose sibling of HollowScriptEditor.tsx's REPL input control):
// line numbers, folding, an optional minimap, Ctrl/Cmd+S to save,
// Ctrl/Cmd+Enter to run, Monaco's own find/replace, format, go-to-symbol
// and go-to-line, and one Monaco model per file keyed by a real URI so
// distinct files keep separate undo histories and cursor positions across
// tab switches - exactly like an IDE's open-file buffers.
//
// Reuses every piece of shared Monaco wiring HollowScriptEditor.tsx already
// established (`monacoSetup.ts`'s worker configuration, `monacoAdapter.ts`'s
// language registration/capability-aware completion+hover/symbols/marker
// updates) - only the editor's own construction options and model lifecycle
// differ. Registers itself with `editorCommands.ts` while mounted so the
// Output, Problems, Outline and Reference panels can jump to a position or
// insert text without a React parent in common.
import { useEffect, useRef, useState } from 'react';
import type * as Monaco from 'monaco-editor';
import type { StudioCapabilityName } from '../../../../engine/scripting/capabilityNames.js';
import { releaseAnalysis } from '../../../../engine/scripting/monaco/index.js';
import { useEditorCommands } from '../editorCommands.js';
import { ensureMonacoWorkerConfigured } from './monacoSetup.js';
import { buildScriptModules, scriptModulePath } from '../../../../engine/scripting/scriptModules.js';
import { useScriptStore } from '../../../scripting/scriptStore.js';
import {
  bindHollowScriptModel,
  HOLLOWSCRIPT_LANGUAGE_ID,
  hollowScriptAnalysisKey,
  hollowScriptFileUri,
  registerHollowScriptLanguage,
  setActiveHollowScriptCapabilities,
  updateHollowScriptMarkers,
} from './monacoAdapter.js';

const DIAGNOSTICS_DEBOUNCE_MS = 300;

export interface HollowScriptFileEditorProps {
  /** Stable file identity (the script's id) - becomes this file's Monaco
   * model URI and its diagnostics `entryPath`, so switching which file is
   * shown creates/reuses a distinct model rather than repointing one shared
   * anonymous model at different text (which would discard undo history on
   * every file switch). */
  readonly fileId: string;
  /** Display name only (used for the diagnostics entryPath's extension) -
   * not part of the model's identity, so renaming a file does not itself
   * force a new model/lost undo history. */
  readonly fileName: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Ctrl/Cmd+Enter - "run this file", the notebook/code-editor convention.
   * Plain Enter inserts a newline as normal. */
  readonly onRunRequested?: () => void;
  /** Ctrl/Cmd+S. Bound inside Monaco so the browser's own "save page"
   * dialog never appears while the caret is in the editor. */
  readonly onSaveRequested?: () => void;
  readonly capabilities: ReadonlySet<StudioCapabilityName> | undefined;
  readonly theme: 'light' | 'dark';
  readonly readOnly?: boolean;
  readonly wordWrap?: boolean;
  readonly minimap?: boolean;
  readonly fontSize?: number;
}

/** One Monaco model per fileId, disposed when no longer referenced. Models
 * persist across a HollowScriptFileEditor remount (e.g. switching away from
 * and back to the Code preset) so undo history and cursor position survive
 * exactly like a real IDE's open-file buffers - only actually disposed via
 * `releaseModel` once a file is deleted (see the roster store's deleteScript
 * flow). */
const modelsByFileId = new Map<string, Monaco.editor.ITextModel>();
/** Where the caret and scroll were when a file was last shown, restored
 * when it is shown again - closing and reopening a tab, or switching
 * workspaces, must not drop the creator back at line 1. */
const viewStateByFileId = new Map<string, Monaco.editor.ICodeEditorViewState>();

function modelFor(monaco: typeof Monaco, fileId: string, value: string): Monaco.editor.ITextModel {
  const existing = modelsByFileId.get(fileId);
  if (existing && !existing.isDisposed()) return existing;
  const uri = hollowScriptFileUri(monaco, fileId);
  const model = monaco.editor.getModel(uri) ?? monaco.editor.createModel(value, HOLLOWSCRIPT_LANGUAGE_ID, uri);
  modelsByFileId.set(fileId, model);
  return model;
}

/** Disposes and forgets a file's Monaco model - call when a script is
 * deleted from the roster, so its model doesn't leak forever in
 * `modelsByFileId`. Safe to call for a fileId with no live model. */
export function releaseHollowScriptFileModel(fileId: string): void {
  const model = modelsByFileId.get(fileId);
  if (model && !model.isDisposed()) model.dispose();
  modelsByFileId.delete(fileId);
  viewStateByFileId.delete(fileId);
  // The model's own disposal releases its analysis handle too; this covers a
  // file whose model was never created, or was already disposed elsewhere.
  releaseAnalysis(hollowScriptAnalysisKey(fileId));
}

/** The sibling scripts `fileName`'s `import`s resolve against, keeping the
 * previous map's identity when nothing in it changed - the roster store
 * replaces its `scripts` array on every keystroke, and a fresh map each time
 * would defeat the diagnostics debounce that depends on this by reference. */
function useSiblingModules(fileName: string): Readonly<Record<string, string>> {
  const scripts = useScriptStore((s) => s.scripts);
  const previous = useRef<Readonly<Record<string, string>>>({});
  const next = buildScriptModules(scripts, fileName);
  const nextKeys = Object.keys(next);
  const previousKeys = Object.keys(previous.current);
  const same = nextKeys.length === previousKeys.length && nextKeys.every((key) => previous.current[key] === next[key]);
  if (!same) previous.current = next;
  return previous.current;
}

export function HollowScriptFileEditor({
  fileId,
  fileName,
  value,
  onChange,
  onRunRequested,
  onSaveRequested,
  capabilities,
  theme,
  readOnly = false,
  wordWrap = false,
  minimap = true,
  fontSize = 13,
}: HollowScriptFileEditorProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof Monaco | null>(null);
  const onChangeRef = useRef(onChange);
  const onRunRequestedRef = useRef(onRunRequested);
  const onSaveRequestedRef = useRef(onSaveRequested);
  const capabilitiesRef = useRef(capabilities);
  const markerTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [ready, setReady] = useState(false);
  const modules = useSiblingModules(fileName || fileId);
  const modulesRef = useRef(modules);
  modulesRef.current = modules;

  onChangeRef.current = onChange;
  onRunRequestedRef.current = onRunRequested;
  onSaveRequestedRef.current = onSaveRequested;
  capabilitiesRef.current = capabilities;

  const entryPath = `${fileName || fileId}.hws`;
  const entryPathRef = useRef(entryPath);
  entryPathRef.current = entryPath;

  // One real editor instance per mounted fileId (a fresh model is attached
  // whenever `fileId` changes) so switching files swaps to that file's own
  // model/undo history rather than overwriting one shared model's text.
  useEffect(() => {
    let disposed = false;
    let unregister: (() => void) | undefined;
    let unbindModel: (() => void) | undefined;
    const restoreCapabilities = setActiveHollowScriptCapabilities(() => capabilitiesRef.current);

    void (async () => {
      ensureMonacoWorkerConfigured();
      const monaco = await import('monaco-editor');
      // Registers the language, then waits for the native tooling wasm the
      // tokeniser needs, so the first paint is already highlighted.
      await registerHollowScriptLanguage(monaco);
      if (disposed || !containerRef.current) return;
      monacoRef.current = monaco;

      const model = modelFor(monaco, fileId, value);
      if (model.getValue() !== value) model.setValue(value);
      // This model's own name, siblings and analysis handle, for the
      // providers (hover, completion, go to definition, outline) to read.
      unbindModel = bindHollowScriptModel(model, () => ({
        entryPath: entryPathRef.current,
        modules: modulesRef.current,
        analysisKey: hollowScriptAnalysisKey(fileId),
        uriForModule: (path) => {
          const target = useScriptStore.getState().scripts.find((script) => scriptModulePath(script.name) === path);
          return target ? hollowScriptFileUri(monaco, target.id) : undefined;
        },
      }));

      const editor = monaco.editor.create(containerRef.current, {
        model,
        readOnly,
        theme: theme === 'dark' ? 'hollowscript-dark' : 'hollowscript-light',
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize,
        lineHeight: Math.round(fontSize * 1.6),
        fontLigatures: true,
        lineNumbers: 'on',
        lineNumbersMinChars: 3,
        glyphMargin: false,
        folding: true,
        showFoldingControls: 'mouseover',
        minimap: { enabled: minimap, renderCharacters: false, maxColumn: 80 },
        scrollBeyondLastLine: false,
        automaticLayout: true,
        wordWrap: wordWrap ? 'on' : 'off',
        // The HollowScript formatter indents with four spaces (format.ts);
        // the editor matches so Format never re-indents what was typed.
        tabSize: 4,
        insertSpaces: true,
        detectIndentation: false,
        padding: { top: 12, bottom: 12 },
        renderLineHighlight: 'line',
        cursorBlinking: 'smooth',
        cursorSmoothCaretAnimation: 'on',
        smoothScrolling: true,
        bracketPairColorization: { enabled: true },
        guides: { bracketPairs: 'active', indentation: true },
        suggest: { showWords: false, preview: true },
        quickSuggestions: { other: true, comments: false, strings: false },
        scrollbar: { verticalScrollbarSize: 10, horizontalScrollbarSize: 10, useShadows: false },
        overviewRulerBorder: false,
        stickyScroll: { enabled: false },
        fixedOverflowWidgets: true,
      });
      editorRef.current = editor;

      const savedView = viewStateByFileId.get(fileId);
      if (savedView) editor.restoreViewState(savedView);

      editor.onDidChangeModelContent(() => {
        const next = model.getValue();
        onChangeRef.current(next);
        if (markerTimeoutRef.current !== undefined) clearTimeout(markerTimeoutRef.current);
        markerTimeoutRef.current = setTimeout(() => {
          void updateHollowScriptMarkers(monaco, model, capabilitiesRef.current, entryPathRef.current, modulesRef.current);
        }, DIAGNOSTICS_DEBOUNCE_MS);
      });

      const commands = useEditorCommands.getState();
      editor.onDidChangeCursorPosition((e) => commands.setCursor(e.position.lineNumber, e.position.column));
      commands.setCursor(editor.getPosition()?.lineNumber ?? 1, editor.getPosition()?.column ?? 1);

      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => onRunRequestedRef.current?.());
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => onSaveRequestedRef.current?.());

      unregister = commands.register({
        fileId,
        revealPosition: (line, column) => {
          const position = { lineNumber: Math.max(1, line), column: Math.max(1, column) };
          editor.setPosition(position);
          editor.revealPositionInCenterIfOutsideViewport(position);
        },
        insertText: (text) => {
          const selection = editor.getSelection();
          if (!selection) return;
          editor.executeEdits('reference', [{ range: selection, text, forceMoveMarkers: true }]);
        },
        trigger: (actionId) => {
          editor.focus();
          editor.trigger('code-workspace', actionId, undefined);
        },
        focus: () => editor.focus(),
      });

      // A jump into this file requested before it was on screen.
      const pending = commands.consumePendingReveal(fileId);
      if (pending) {
        const position = { lineNumber: pending.line, column: pending.column };
        editor.setPosition(position);
        editor.revealPositionInCenterIfOutsideViewport(position);
      }
      editor.focus();

      void updateHollowScriptMarkers(monaco, model, capabilitiesRef.current, entryPathRef.current, modulesRef.current);
      setReady(true);
    })();

    return () => {
      disposed = true;
      restoreCapabilities();
      unregister?.();
      unbindModel?.();
      if (markerTimeoutRef.current !== undefined) clearTimeout(markerTimeoutRef.current);
      const editor = editorRef.current;
      if (editor) {
        const view = editor.saveViewState();
        if (view) viewStateByFileId.set(fileId, view);
        // Dispose the editor widget only - the model is intentionally kept
        // alive in modelsByFileId (see its own doc comment) so switching
        // back to this file later resumes its undo history.
        editor.dispose();
      }
      editorRef.current = null;
      setReady(false);
    };
    // Re-runs only on fileId: every other prop is synced live into the
    // existing editor instance by its own effect below - recreating the
    // whole Monaco widget on a theme or font-size change would be wasteful
    // and would drop the caret.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId]);

  // Syncs an externally-driven `value` change (e.g. scriptStore's
  // resetActiveToSource, or a roster reload) into the model when it didn't
  // originate from this editor's own onDidChangeModelContent.
  useEffect(() => {
    const model = editorRef.current?.getModel();
    if (!model || model.getValue() === value) return;
    model.setValue(value);
  }, [value]);

  useEffect(() => {
    editorRef.current?.updateOptions({ readOnly });
  }, [readOnly]);

  useEffect(() => {
    editorRef.current?.updateOptions({
      wordWrap: wordWrap ? 'on' : 'off',
      minimap: { enabled: minimap, renderCharacters: false, maxColumn: 80 },
      fontSize,
      lineHeight: Math.round(fontSize * 1.6),
    });
  }, [wordWrap, minimap, fontSize]);

  useEffect(() => {
    monacoRef.current?.editor.setTheme(theme === 'dark' ? 'hollowscript-dark' : 'hollowscript-light');
  }, [theme]);

  useEffect(() => {
    const monaco = monacoRef.current;
    const model = editorRef.current?.getModel();
    if (!monaco || !model) return;
    void updateHollowScriptMarkers(monaco, model, capabilities, entryPath, modules);
  }, [capabilities, entryPath, modules, ready]);

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      {!ready ? (
        <div
          style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'var(--ss-mono)', fontSize: 12, color: 'var(--ss-text-3)', pointerEvents: 'none',
          }}
        >
          Loading editor…
        </div>
      ) : null}
    </div>
  );
}
